import { useRef, useState, useEffect, useCallback, RefObject } from 'react';
import useNotification from './useNotification';

export type AudioRecorderConfig = {
    // 是否启用自动停止
    autoStop?: boolean;
    // 静音阈值(dBFS)
    silenceThreshold?: number;
    // 校准时间(ms)
    calibrationTime?: number;
    // 无声音超时(ms)
    inactivityTimeout?: number;
    // 多级预警时间数组(ms)
    warnLevels?: number[];
    lang?: string;
    onInactivity?: () => void;
    canvasRef?: RefObject<HTMLCanvasElement>;
};

const useAudioRecorder = (config?: AudioRecorderConfig) => {
    // 配置合并
    const {
        autoStop = false,
        silenceThreshold = -45,
        calibrationTime = 3000,
        inactivityTimeout = 5000,
        warnLevels = [ 2000, 4000 ],
        canvasRef,
        // lang = 'zh-CN',
        lang = navigator.language || 'en-US',
        onInactivity
    } = config || {};

    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioChunks = useRef<Blob[]>([]);
    const audioContextRef = useRef<AudioContext | null>(null);
    const analyserRef = useRef<AnalyserNode | null>(null);
    const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
    const workerRef = useRef<Worker>();
    const animationFrameId = useRef<number>();
    const lastActivityTimeRef = useRef(Date.now());

    const [ isRecording, setIsRecording ] = useState(false);
    const [ audioBlob, setAudioBlob ] = useState<Blob>();
    const [ transcription, setTranscription ] = useState<string>('');
    const [ warningLevel, setWarningLevel ] = useState(-1);

    const notification = useNotification();
    const recognitionRef = useRef<any>(null);

    const checkPermissions = async () => {
        try {
            const permission = await navigator.permissions.query({ name: 'microphone' as any });
            if (permission.state === 'denied') {
                notification.warning('麦克风访问被拒绝，请手动启用权限');
                return false;
            }
            return true;
        } catch (e) {
            console.warn('权限API不可用');
            return true;
        }
    };

    const getAudioStream = async () => {
        try {
            return await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    sampleRate: 44100
                },
                video: false
            });
        } catch (err: any) {
            if (err.name === 'NotAllowedError') {
                notification.warning('用户拒绝授予麦克风权限');
            } else if (err.name === 'NotFoundError') {
                notification.warning('未找到可用的麦克风设备');
            } else {
                notification.warning(`获取音频流失败: ${err.message}`);
            }
            return null;
        }
    };

    // Worker消息处理
    const handleWorkerMessage = useCallback((e: MessageEvent) => {
        switch (e.data.type) {
            case 'calibrated':
                console.info(`环境校准完成，阈值：${e.data.threshold.toFixed(1)}dB`);
                break;
            case 'warn':
                setWarningLevel(e.data.level);
                break;
            case 'activity':
                lastActivityTimeRef.current = e.data.curActivityTime;
                setWarningLevel(-1);
                break;
        }
    }, []);

    const initSpeechRecognition = useCallback(() => {
        const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
        if (!SpeechRecognition) {
            notification.warning('浏览器不支持语音识别功能');
            return null;
        }

        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = lang;

        recognition.onresult = (event) => {
            let finalTranscript = '';
            for (let i = event.resultIndex; i < event.results.length; i++) {
                const transcript = event.results[i][0].transcript;
                if (event.results[i].isFinal) {
                    finalTranscript += transcript;
                }
            }
            setTranscription((prev) => prev + finalTranscript);
        };

        recognition.onerror = (event) => {
            console.error('SpeechRecognition error:', event.error);
            notification.error(`语音识别错误: ${event.error}`);
        };

        recognition.onend = () => {
            console.info('语音识别已结束');
        };

        return recognition;
    }, [ notification ]);

    // 初始化音频分析
    const initAudioAnalysis = useCallback((stream: MediaStream) => {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
        analyserRef.current = audioContextRef.current.createAnalyser();
        analyserRef.current.fftSize = 2048;

        sourceRef.current = audioContextRef.current.createMediaStreamSource(stream);
        sourceRef.current.connect(analyserRef.current);

        workerRef.current?.postMessage({
            type: 'init',
            config: { silenceThreshold, warnLevels }
        });
    }, []);

    // 开始录音
    const startRecording = useCallback(async () => {
        if (!(await checkPermissions())) return;

        const stream = await getAudioStream();
        if (!stream) return;

        try {
            // 初始化录音器
            mediaRecorderRef.current = new MediaRecorder(stream, {
                mimeType: MediaRecorder.isTypeSupported('audio/webm')
                    ? 'audio/webm'
                    : 'audio/mp4'
            });

            // 重置转化文本
            setTranscription('');

            // 初始化音频分析
            initAudioAnalysis(stream);
            startAudioProcessing();

            // Initialize speech recognition
            recognitionRef.current = initSpeechRecognition();
            recognitionRef.current?.start();

            // 设置录音数据收集
            mediaRecorderRef.current.ondataavailable = (e) => {
                audioChunks.current.push(e.data);
            };

            mediaRecorderRef.current.start(1000);
            setIsRecording(true);

            // 开始环境校准
            setTimeout(() => {
                workerRef.current?.postMessage({ type: 'calibrate' });
            }, calibrationTime);
        } catch (err: any) {
            notification.error(`录音初始化失败: ${err.message}`);
            stream.getTracks().forEach((track) => track.stop());
        }
    }, [ initAudioAnalysis, initSpeechRecognition ]);

    // 启动音频处理
    const startAudioProcessing = useCallback(() => {
        const processAudio = () => {
            if (!analyserRef.current) return;

            const buffer = new Float32Array(analyserRef.current.fftSize);
            analyserRef.current.getFloatTimeDomainData(buffer);

            workerRef.current?.postMessage({
                type: 'data',
                data: buffer
            });

            // 可视化绘制
            if (canvasRef?.current) {
                drawWaveLine(canvasRef.current, buffer);
            }

            animationFrameId.current = requestAnimationFrame(processAudio);
        };

        processAudio();
    }, []);

    // 可视化绘制方法
    const drawWaveLine = useCallback((canvas: HTMLCanvasElement, buffer: Float32Array) => {
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.beginPath();

        const sliceWidth = canvas.width / buffer.length;
        let x = 0;

        for (let i = 0; i < buffer.length; i++) {
            const y = (buffer[i] * canvas.height) / 2 + canvas.height / 2;
            i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
            x += sliceWidth;
        }

        ctx.strokeStyle = '#4CAF50';
        ctx.lineWidth = 2;
        ctx.stroke();
    }, []);

    // 停止录音
    const stopRecording = useCallback(() => {
        if (mediaRecorderRef.current?.state === 'recording') {
            mediaRecorderRef.current.stop();
            mediaRecorderRef.current.stream.getTracks().forEach((track) => track.stop());
            setIsRecording(false);

            mediaRecorderRef.current.onstop = () => {
                const blob = new Blob(audioChunks.current, {
                    type: mediaRecorderRef.current?.mimeType
                });
                audioChunks.current = [];
                setAudioBlob(blob);
            };

            // Stop speech recognition
            recognitionRef.current?.stop();

            // 清理资源
            if (animationFrameId.current) {
                cancelAnimationFrame(animationFrameId.current);
            }
            if (audioContextRef.current) {
                audioContextRef.current.close();
            }
        }
    }, []);

    // 初始化Web Worker (暂时禁用)
    useEffect(() => {
        // TODO: 实现 audioRecorder.worker.ts
        // workerRef.current = new Worker(new URL('@/workers/audioRecorder.worker.ts', import.meta.url));
        // workerRef.current.onmessage = handleWorkerMessage;
        // return () => workerRef.current?.terminate();
    }, []);

    // 自动停止检测
    useEffect(() => {
        if (!autoStop || !isRecording) return;

        const timer = setInterval(() => {
            if (Date.now() - lastActivityTimeRef.current > inactivityTimeout) {
                onInactivity && onInactivity();
                console.info('超时事件');
            }
        }, 1000);

        return () => clearInterval(timer);
    }, [ autoStop, isRecording ]);

    return {
        isRecording,
        audioBlob,
        text: transcription,
        warningLevel,
        canvasRef,
        start: startRecording,
        stop: stopRecording
    };
};

export default useAudioRecorder;
